<template>
  <div v-if="show && displayActionActive">
    <a
      :href="outboundLink"
      data-testid="viewer-cta"
      :target="target || '_blank'"
      :class="`bg-[${background}] text-[${accessibleColor}] ${buttonClass}`"
      :style="{ background, color: accessibleColor }"
      class="inline-block items-center gap-2 px-2 sm:ml-0 sm:px-4 py-2 text-xs sm:text-sm font-bold leading-4 border-2 border-transparent outline-none relative z-10"
    >
      {{ buttonText }}
    </a>
  </div>
</template>
<script setup lang="ts">
import { useCallToActionStore } from './../store/useCallToActionStore';
import { storeToRefs } from 'pinia';
import { watch, onMounted, computed } from 'vue';
import { useViewerItemStore } from '../store/useViewerItemStore';

const callToActionStore = useCallToActionStore();
const props = defineProps<{
  hoverOnly: boolean;
}>();

const itemStore = useViewerItemStore();

const show = computed(() => {
  if (showHoverableButton.value) {
    return true;
  }

  if (props.hoverOnly && !isHoverable.value) {
    return false;
  }
  if (!props.hoverOnly && isHoverable.value) {
    return false;
  }

  return true;
});

const loadCta = (val: string | undefined) => val && callToActionStore.getCallToAction(val);

onMounted(() => loadCta(itemStore.ctaId));
watch(() => itemStore.ctaId, loadCta);

const { target, isHoverable, background, color, buttonText, buttonClass, displayActionActive, outboundLink, showHoverableButton }: any =
  storeToRefs(callToActionStore);

// Compute accessible text color based on background luminance
const accessibleColor = computed(() => {
  const bg = background.value;
  if (!bg) {
    return color.value;
  }

  // Parse hex color to RGB
  const hex = bg.replace('#', '');
  if (hex.length < 6) {
    return color.value;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (WCAG formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // If the background is light and the chosen text color is also light, use dark text
  const textHex = (color.value || '#FFFFFF').replace('#', '');
  if (textHex.length >= 6) {
    const tr = parseInt(textHex.substring(0, 2), 16);
    const tg = parseInt(textHex.substring(2, 4), 16);
    const tb = parseInt(textHex.substring(4, 6), 16);
    const textLuminance = (0.299 * tr + 0.587 * tg + 0.114 * tb) / 255;

    // If both background and text are light, switch to dark text
    if (luminance > 0.6 && textLuminance > 0.6) {
      return '#1a1a1a';
    }

    // If both background and text are dark, switch to light text
    if (luminance < 0.4 && textLuminance < 0.4) {
      return '#FFFFFF';
    }
  }

  return color.value;
});
</script>
<style lang="scss">
#cta-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
